import React from 'react';
import IconCollection from '../IconCollection/IconCollection';
import css from './AdjustedBill.module.css';

const formatTimeAgo = date => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (Number.isNaN(seconds) || seconds < 0) return '';

  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  if (minutes > 0) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  return 'Just now';
};

const getTransitionCreatedAt = (txTransitions, transitionName) => {
  const found = Array.isArray(txTransitions)
    ? txTransitions.find(t => t?.transition === transitionName)
    : null;
  return found?.createdAt ? new Date(found.createdAt) : null;
};

const getActivityConfigForTransition = transitionName => {
  switch (transitionName) {
    case 'transition/customer-make-counter-offer':
      return {
        title: 'Counter offer submitted',
        icon: 'time_icon',
        variant: 'pending',
      };
    case 'transition/provider-make-counter-offer':
      return {
        title: 'Provider offer submitted',
        icon: 'time_icon',
        variant: 'pending',
      };
    case 'transition/provider-accept-counter-offer':
      return {
        title: 'Counter offer accepted',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/customer-reject-offer':
      return {
        title: 'Offer rejected',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/confirm-payment':
      return {
        title: 'Payment confirmed',
        icon: 'tick_icon',
        variant: 'completed',
      };
    // Invoice-specific transitions (overtime/expense invoices)
    case 'transition/customer-counter-invoice':
      return {
        title: 'Negotiation started',
        icon: 'time_icon',
        variant: 'pending',
      };
    case 'transition/provider-counter-invoice':
      return {
        title: 'Vendor counter offer submitted',
        icon: 'time_icon',
        variant: 'pending',
      };
    case 'transition/provider-accept-invoice-counter':
      return {
        title: 'Negotiated amount accepted by vendor',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/provider-reject-invoice':
      return {
        title: 'Negotiation rejected by vendor',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/customer-reject-invoice':
      return {
        title: 'Invoice declined',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/request-invoice-payment':
      return {
        title: 'Payment started',
        icon: 'time_icon',
        variant: 'pending',
      };
    case 'transition/confirm-invoice-payment':
      return {
        title: 'Invoice paid',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/deliver':
      return {
        title: 'Marked delivered',
        icon: 'tick_icon',
        variant: 'completed',
      };
    case 'transition/accept-deliverable':
      return {
        title: 'Marked complete',
        icon: 'tick_icon',
        variant: 'completed',
      };
    default:
      return null;
  }
};

const INVOICE_ACTIVITY_COPY = {
  overtime: {
    title: 'Overtime charge submitted',
    description: 'An adjusted bill with overtime charges was submitted',
  },
  expense: {
    title: 'Expense coverage submitted',
    description: 'An adjusted bill with additional expenses was submitted',
  },
  reduction: {
    title: 'Bill reduction submitted',
    description: 'A bill reduction credit was submitted',
  },
};

const AdjustedBillActivity = props => {
  const { transaction, isOvertimeInvoice, invoiceType } = props;

  // Prefer the explicit invoiceType; fall back to the legacy isOvertimeInvoice prop.
  const resolvedInvoiceType = invoiceType || (isOvertimeInvoice ? 'overtime' : null);

  const txAttrs = transaction?.attributes;
  const txTransitions = txAttrs?.transitions || [];
  const offers = txAttrs?.metadata?.offers || [];

  const activities = [];

  if (resolvedInvoiceType) {
    const copy = INVOICE_ACTIVITY_COPY[resolvedInvoiceType] || INVOICE_ACTIVITY_COPY.overtime;
    const createdAt = txAttrs?.createdAt ? new Date(txAttrs.createdAt) : null;
    activities.push({
      key: 'invoice-submitted',
      title: copy.title,
      description: copy.description,
      createdAt,
      variant: 'pending',
      icon: 'time_icon',
    });
  }

  // Build offer activities from metadata.offers and transitions timestamps
  if (Array.isArray(offers)) {
    offers.forEach((offer, index) => {
      const transitionName = offer?.transition;
      const cfg = getActivityConfigForTransition(transitionName);
      if (!cfg) return;

      const createdAt = getTransitionCreatedAt(txTransitions, transitionName);

      const isFinal = !!offer?.isFinalOffer;
      const amount = typeof offer?.offerInSubunits === 'number' ? offer.offerInSubunits / 100 : null;
      const message = offer?.offerMessage;

      const descriptionParts = [];
      if (typeof amount === 'number') descriptionParts.push(`Offer: $${amount}`);
      if (isFinal) descriptionParts.push('Final offer');
      if (message && String(message).trim().length > 0) descriptionParts.push(String(message).trim());

      activities.push({
        key: `offer-${index}-${transitionName}`,
        title: cfg.title,
        description: descriptionParts.join(' - '),
        createdAt,
        variant: cfg.variant,
        icon: cfg.icon,
      });
    });
  }

  // Add major lifecycle transitions
  [
    'transition/confirm-payment',
    'transition/deliver',
    'transition/accept-deliverable',
    // Invoice-specific lifecycle transitions
    'transition/request-invoice-payment',
    'transition/confirm-invoice-payment',
    'transition/provider-accept-invoice-counter',
    'transition/provider-reject-invoice',
    'transition/customer-reject-invoice',
  ].forEach(
    transitionName => {
      const cfg = getActivityConfigForTransition(transitionName);
      const createdAt = getTransitionCreatedAt(txTransitions, transitionName);
      if (!cfg || !createdAt) return;

      activities.push({
        key: transitionName,
        title: cfg.title,
        description: '',
        createdAt,
        variant: cfg.variant,
        icon: cfg.icon,
      });
    }
  );

  const sorted = activities
    .filter(a => a.createdAt)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  if (sorted.length === 0) return null;

  return (
    <div className={css.recentActivitiesSection}>
      <h3 className={css.recentActivitiesTitle}>Recent Activities</h3>

      {sorted.map(item => {
        const iconClass =
          item.variant === 'completed' ? css.activityIconCompleted : css.activityIconPending;

        return (
          <div key={item.key} className={css.activityItem}>
            <div className={css.activityIcon}>
              <div className={iconClass}>
                <IconCollection icon={item.icon} />
              </div>
            </div>
            <div className={css.activityContent}>
              <div className={css.activityTitle}>{item.title}</div>
              {item.description ? (
                <div className={css.activityDescription}>{item.description}</div>
              ) : null}
              <div className={css.activityTime}>{formatTimeAgo(item.createdAt)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdjustedBillActivity;
